"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SalesOrderHandler = exports.TEMPORARY_SALES_ORDER_ID = void 0;
const React = require("react");
const react_1 = require("react");
const SalesOrder_1 = require("../../models/SalesOrder");
const SalesOrderItemFixed_1 = require("../../models/SalesOrderItemFixed");
const TSalesOrderType_1 = require("../../types/TSalesOrderType");
const TSalesOrderItemType_1 = require("../../types/TSalesOrderItemType");
const SalesOrderContext_1 = require("../../contexts/SalesOrderContext");
const PersistenceContext_1 = require("../../../../../contexts/PersistenceContext");
const RetailLocationsContext_1 = require("../../contexts/RetailLocationsContext");
const OrderTypeContext_1 = require("../../contexts/OrderTypeContext");
const ProductsContext_1 = require("../../contexts/ProductsContext");
const currentUnixTimestamp_1 = require("../../../../../apis/spg/util/currentUnixTimestamp");
const isDeliverySalesOrderItem_1 = require("../../helpers/isDeliverySalesOrderItem");
const isServiceSalesOrderItem_1 = require("../../helpers/isServiceSalesOrderItem");
const useClient_1 = require("../../../../../hooks/useClient");
const useCreateSalesOrder_1 = require("../../hooks/useCreateSalesOrder");
const useRetrieveTemporarySalesOrder_1 = require("../../hooks/useRetrieveTemporarySalesOrder");
const useSetTemporarySalesOrder_1 = require("../../hooks/useSetTemporarySalesOrder");
const RetailCustomer_1 = require("../../models/RetailCustomer");
const rebuildSalesOrderItems_1 = require("../../helpers/rebuildSalesOrderItems");
const errorTypes_1 = require("../../client/errorTypes");
const useRequiredContext_1 = require("../../../../../hooks/useRequiredContext");
const ErrorResolver_1 = require("../../../../../contexts/ErrorResolver");
const constants_1 = require("../../helpers/constants");
const string_1 = require("../../../../../helpers/string");
const CustomerPaymentMethodContext_1 = require("../../contexts/CustomerPaymentMethodContext");
const useTickReRender_1 = require("../../../../../hooks/useTickReRender");
const usePreviousValue_1 = require("../../../../../hooks/usePreviousValue");
const context_1 = require("../CustomerLocationHandler/context");
exports.TEMPORARY_SALES_ORDER_ID = "temporarySalesOrderId";
const SalesOrderHandler = ({ currentTemporarySalesOrderId, children }) => {
    var _a, _b, _c;
    const client = (0, useClient_1.useClient)();
    const tick = (0, useTickReRender_1.useTickReRender)();
    const { deliveryId, serviceId } = env;
    const { getItem, setItem, removeItem } = (0, react_1.useContext)(PersistenceContext_1.PersistenceContext);
    const { retailLocation, retailLocations, requestedRetailLocationSlug, setRetailLocation } = (0, react_1.useContext)(RetailLocationsContext_1.RetailLocationsContext);
    const { products, deliveryProduct, serviceProduct } = (0, react_1.useContext)(ProductsContext_1.ProductsContext);
    const { orderType, setOrderType, resetOrderType } = (0, react_1.useContext)(OrderTypeContext_1.OrderTypeContext);
    const { resolveError } = (0, useRequiredContext_1.useRequiredContext)(ErrorResolver_1.ErrorResolverContext);
    const { setPaymentMethod } = (0, useRequiredContext_1.useRequiredContext)(CustomerPaymentMethodContext_1.CustomerPaymentMethodContext);
    const { customerLocation } = (0, useRequiredContext_1.useRequiredContext)(context_1.CustomerLocationContext);
    const [salesOrderItems, setSalesOrderItems] = (0, react_1.useState)([]);
    const [temporarySalesOrderId, setTemporarySalesOrderId] = (0, react_1.useState)(currentTemporarySalesOrderId);
    const [salesOrderItemForCartRecovery, setSalesOrderItemForCartRecovery] = (0, react_1.useState)(undefined);
    const [promotions, setPromotions] = (0, react_1.useState)(undefined);
    const [totalPrice, setTotalPrice] = (0, react_1.useState)(0);
    const [isPerformingCartRecovery, setIsPerformingCartRecovery] = (0, react_1.useState)(true);
    const [hasPerformedCartRecovery, setHasPerformedCartRecovery] = (0, react_1.useState)(false);
    const [isReorder, setIsReorder] = (0, react_1.useState)(null);
    const [hasSalesOrderError, setHasSalesOrderError] = (0, react_1.useState)(false);
    const [cartNoLongerExists, setCartNoLongerExists] = (0, react_1.useState)(false);
    const [requestedReadyTime, setRequestedReadyTime] = (0, react_1.useState)((0, currentUnixTimestamp_1.currentUnixTimestamp)());
    const defaultNotes = (_c = (_b = (_a = client.auth) === null || _a === void 0 ? void 0 : _a.session) === null || _b === void 0 ? void 0 : _b.retailCustomer) === null || _c === void 0 ? void 0 : _c.defaultNotes;
    const [notes, setNotes] = (0, react_1.useState)(defaultNotes);
    const [temporaryAddress, setTemporaryAddress] = (0, react_1.useState)(null);
    const processingOrder = (0, react_1.useRef)(false);
    const processingTemporarySalesOrder = (0, react_1.useRef)(false);
    const previousCart = (0, react_1.useRef)(null);
    const totalItems = (0, react_1.useMemo)(() => salesOrderItems.reduce((total, salesOrderItem) => (0, isDeliverySalesOrderItem_1.isDeliverySalesOrderItem)(salesOrderItem, deliveryProduct.productId) ||
        (0, isServiceSalesOrderItem_1.isServiceSalesOrderItem)(salesOrderItem, serviceProduct.productId)
        ? total
        : total + salesOrderItem.quantity, 0), [salesOrderItems]);
    const hasCart = (0, react_1.useMemo)(() => retailLocation && orderType && totalItems > 0, [
        retailLocation,
        orderType,
        totalItems
    ]);
    (0, react_1.useEffect)(() => {
        const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        if (requestedReadyTime < timestampNow) {
            setRequestedReadyTime(tick);
        }
    }, [tick]);
    (0, react_1.useEffect)(() => {
        var _a, _b;
        if (!((_b = (_a = client.auth) === null || _a === void 0 ? void 0 : _a.session) === null || _b === void 0 ? void 0 : _b.retailCustomer)) {
            return;
        }
        const { auth: { session: { retailCustomer: { defaultNotes } } } } = client;
        if (!defaultNotes) {
            return;
        }
        setNotes(defaultNotes);
    }, [client.auth.session]);
    (0, react_1.useEffect)(() => {
        if (hasPerformedCartRecovery || // don't perform cart recovery more than once
            !retailLocations || // don't perform cart recovery until retail locations are loaded
            !orderType ||
            (requestedRetailLocationSlug && !retailLocation) // don't perform cart recovery until pre-selected retail location is set if location pre-selection is provided
        ) {
            return;
        }
        // make one-off attempt on the first load to perform cart recovery
        (async () => {
            await performCartRecovery();
            setIsPerformingCartRecovery(false);
        })();
    }, [retailLocation, retailLocations, orderType]);
    (0, react_1.useEffect)(() => {
        // update temporary sales order id in local storage when it changes
        if (temporarySalesOrderId && setItem) {
            setItem(exports.TEMPORARY_SALES_ORDER_ID, temporarySalesOrderId);
        }
    }, [temporarySalesOrderId]);
    const getSavedTemporarySalesOrderId = (0, react_1.useCallback)(() => {
        return (async () => {
            return await getItem(exports.TEMPORARY_SALES_ORDER_ID);
        })();
    }, []);
    // handle all api errors
    const handleApiError = (0, react_1.useCallback)(async (error) => {
        console.error(error);
        if (error instanceof errorTypes_1.OutOfHoursOrderError) {
            resolveError({
                title: "No longer available",
                description: error.message,
                showSupportContact: false,
                hideCloseIcon: true,
                approach: "modal"
            });
            return;
        }
        if (error instanceof errorTypes_1.PaymentExceededDenied) {
            resolveError({
                title: "Your account has been suspended",
                description: error.message,
                showSupportContact: false,
                canDismiss: false,
                hideCloseIcon: true,
                approach: "modal"
            });
            // finally, logout the customer and clear their cart
            await client.auth.logout();
            await resetCart(true);
            return;
        }
        if (error instanceof errorTypes_1.DeliveryServiceUnavailableError ||
            error instanceof errorTypes_1.DuplicateDeliveryError) {
            resolveError({
                title: "Delivery currently unavailable",
                description: error.message,
                showSupportContact: false,
                hideCloseIcon: true,
                approach: "modal"
            });
            return;
        }
        // the customer has been charged but the sales order could not be completed.
        // the modal let the customer know about it and that they have been refunded
        if (error instanceof errorTypes_1.CompleteSalesOrderError ||
            error instanceof errorTypes_1.SalesOrderPaymentError) {
            const [newTemporarySalesOrderId, paymentErrorMessage] = error.message.split("-");
            let description = error instanceof errorTypes_1.CompleteSalesOrderError
                ? constants_1.TPaymentErrorMessage.CompleteSalesOrderError
                : constants_1.TPaymentErrorMessage.SalesOrderPaymentError;
            if (error instanceof errorTypes_1.SalesOrderPaymentError) {
                description +=
                    "\n\nReason: " + (0, string_1.capitalizeFirstLetter)(paymentErrorMessage);
            }
            resolveError({
                title: undefined,
                description,
                approach: "modal",
                hideCloseIcon: true
            });
            setTemporarySalesOrderId(newTemporarySalesOrderId);
            setHasSalesOrderError(true);
            return;
        }
        // temporary sales order doesn't exist
        if (error.message.includes("sales order does not exist")) {
            setCartNoLongerExists(true);
        }
        // set error state
        setHasSalesOrderError(true);
    }, []);
    // configure useCreateSalesOrder hook
    const { mutate: createSalesOrder, error: processSalesOrderError } = (0, useCreateSalesOrder_1.useCreateSalesOrder)({
        onSuccess: (salesOrder) => {
            return resetCart(true, false).then(() => {
                return salesOrder;
            });
        },
        onSettled: () => {
            processingOrder.current = false;
        },
        onError: handleApiError
    });
    // configure useRetrieveTemporarySalesOrder hook
    const { mutate: _retrieveTemporarySalesOrder, error: retrieveTemporarySalesOrderError, isLoading: isRetrievingTemporarySalesOrder } = (0, useRetrieveTemporarySalesOrder_1.useRetrieveTemporarySalesOrder)({
        onSuccess: (temporarySalesOrder) => {
            return temporarySalesOrder;
        },
        onSettled: () => {
            processingTemporarySalesOrder.current = false;
        },
        onError: handleApiError
    });
    // configure useRetrieveTemporarySalesOrder hook
    const { mutate: _setTemporarySalesOrder, error: setTemporarySalesOrderError, isLoading: isSettingTemporarySalesOrder } = (0, useSetTemporarySalesOrder_1.useSetTemporarySalesOrder)({
        onSuccess: (temporarySalesOrder) => {
            return temporarySalesOrder;
        },
        onSettled: () => {
            processingTemporarySalesOrder.current = false;
        },
        onError: handleApiError
    });
    // update cart following a change to the sales order items
    const updateCart = (0, react_1.useCallback)(async (cart) => {
        // reset cart when it is empty
        if (cart.length === 0) {
            return await resetCart(false);
        }
        // otherwise, query backend for updates
        if (processingTemporarySalesOrder.current) {
            // protect us from unexpected updates
            console.warn("Already processing the temporary sales order");
            return;
        }
        // sanity checks
        if (!retailLocation) {
            throw new Error(constants_1.TSalesOrderUnavailableMessage.StoreLocationUnavailableForOrder);
        }
        const temporarySalesOrder = new SalesOrder_1.SalesOrder();
        temporarySalesOrder.salesOrderId = temporarySalesOrderId;
        temporarySalesOrder.type = orderType;
        temporarySalesOrder.salesOrderItems = cart;
        temporarySalesOrder.retailLocation = retailLocation;
        // using dummy customer without real id, for validation purpose
        temporarySalesOrder.retailCustomer = new RetailCustomer_1.RetailCustomer();
        // values of these timestamps are more for validation and serialisation purpose
        // as they don't matter for temporary sales orders
        temporarySalesOrder.requestedReadyTime = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        temporarySalesOrder.predictedCompletionTime = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        temporarySalesOrder.createdAt = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        temporarySalesOrder.placementTime = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        temporarySalesOrder.temporaryAddress = temporaryAddress;
        processingTemporarySalesOrder.current = true;
        await updateTemporarySalesOrder(temporarySalesOrder);
    }, [retailLocation, temporarySalesOrderId, orderType, temporaryAddress]);
    // prepare to populate cart from a saved temporary sales order id
    const performCartRecovery = (0, react_1.useCallback)(() => {
        return (async () => {
            setHasPerformedCartRecovery(true);
            // abort if no temporary sales order id can be found in local storage
            const savedTemporarySalesOrderId = await getSavedTemporarySalesOrderId();
            if (!savedTemporarySalesOrderId) {
                return;
            }
            // reset cart if no temporary sales order can be retrieved matching the supplied
            // temporary sales order id
            const temporarySalesOrder = await _retrieveTemporarySalesOrder({
                salesOrderId: savedTemporarySalesOrderId,
                orderType
            });
            if (!temporarySalesOrder) {
                await resetCart(true);
                return;
            }
            // reset cart if the current active retail location does not match
            // the location from the temporary sales order
            if (retailLocation &&
                temporarySalesOrder.retailLocation.retailLocationId !==
                    retailLocation.retailLocationId) {
                return resetCart(true);
            }
            // infer the order type by checking the presence of any delivery item
            const { type: temporarySalesOrderOrderType } = temporarySalesOrder;
            // reset cart if the retail location cannot serve this order type
            if ((temporarySalesOrderOrderType === TSalesOrderType_1.TSalesOrderType.Delivery &&
                !temporarySalesOrder.retailLocation.canDeliver) ||
                (temporarySalesOrderOrderType === TSalesOrderType_1.TSalesOrderType.Pickup &&
                    !temporarySalesOrder.retailLocation.canPickup)) {
                return resetCart(true);
            }
            // if all looking good,
            // set retail location, order type and sales order items for cart recovery
            setRetailLocation(temporarySalesOrder.retailLocation);
            setOrderType(temporarySalesOrderOrderType);
            setTemporaryAddress(temporarySalesOrder.temporaryAddress || customerLocation);
            setTemporarySalesOrderId(temporarySalesOrder.salesOrderId);
            setSalesOrderItemForCartRecovery(temporarySalesOrder.salesOrderItems);
            const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
            const { openingTime } = temporarySalesOrder.retailLocation.getOperatingDetailsForTime(timestampNow);
            const requestedReadyTime = openingTime && timestampNow < openingTime ? openingTime : timestampNow;
            setRequestedReadyTime(requestedReadyTime);
        })();
    }, [
        retailLocation,
        getSavedTemporarySalesOrderId,
        _retrieveTemporarySalesOrder,
        setRetailLocation,
        setOrderType,
        setTemporarySalesOrderId,
        setSalesOrderItemForCartRecovery,
        orderType,
        temporaryAddress
    ]);
    // prepare to populate cart from an eligible past sales order
    const populateCart = (salesOrder) => {
        if (salesOrder.canReorder) {
            setIsReorder(salesOrder.completed);
            setRetailLocation(salesOrder.retailLocation);
            setOrderType(salesOrder.type);
            setRequestedReadyTime((0, currentUnixTimestamp_1.currentUnixTimestamp)());
            setTemporaryAddress(salesOrder.type === TSalesOrderType_1.TSalesOrderType.Delivery
                ? salesOrder.deliveryAddress
                : customerLocation);
            setSalesOrderItemForCartRecovery(salesOrder.salesOrderItems);
        }
    };
    const deleteTemporarySalesOrder = async (salesOrderId) => {
        try {
            await client.retailSalesOrders.deleteTemporarySalesOrder(salesOrderId);
        }
        catch (e) {
            console.log("Failed to Delete Sales Order");
            throw e;
        }
    };
    // populate cart if there are sales order items waiting for cart recovery
    (0, react_1.useEffect)(() => {
        (async () => {
            if (products && salesOrderItemForCartRecovery) {
                setIsPerformingCartRecovery(true);
                // ensure the items being added to the cart actually exist
                const { salesOrderItems: newSalesOrderItems, skipped } = (0, rebuildSalesOrderItems_1.rebuildSalesOrderItems)(salesOrderItemForCartRecovery.map((salesOrderItem) => salesOrderItem.serialize()), products.concat(deliveryProduct, serviceProduct), deliveryProduct.productId, serviceProduct.productId, true);
                // discrepancy found
                if (skipped) {
                    const error = new Error("sales order does not exist");
                    await handleApiError(error);
                }
                previousCart.current = [];
                try {
                    await updateCart(newSalesOrderItems);
                    setSalesOrderItems(newSalesOrderItems);
                    // reset sales order items for cart recovery once they have been recovered
                    setSalesOrderItemForCartRecovery(undefined);
                    setIsPerformingCartRecovery(false);
                }
                catch (e) {
                    console.log("Error updating cart: " + e.message);
                    throw e;
                }
            }
        })();
    }, [products, deliveryProduct, salesOrderItemForCartRecovery, updateCart]);
    // reset cart by deleting the temporary sales order
    // from local storage and the ERP
    const resetCart = (0, react_1.useCallback)(async (resetSalesOrder, resetCartWithErpDelete = true) => {
        if (resetCartWithErpDelete) {
            const savedTemporarySalesOrderId = await getSavedTemporarySalesOrderId();
            if (savedTemporarySalesOrderId) {
                await deleteTemporarySalesOrder(savedTemporarySalesOrderId);
            }
        }
        await removeItem(exports.TEMPORARY_SALES_ORDER_ID);
        setTemporarySalesOrderId(undefined);
        setSalesOrderItems([]);
        setPromotions(undefined);
        setTotalPrice(0);
        setIsPerformingCartRecovery(false);
        setCartNoLongerExists(true);
        setHasSalesOrderError(false);
        if (resetSalesOrder) {
            setRequestedReadyTime((0, currentUnixTimestamp_1.currentUnixTimestamp)());
            setTemporaryAddress(null);
            setRetailLocation(null);
            await resetOrderType();
        }
    }, [setTemporarySalesOrderId, setSalesOrderItems, setPromotions, setTotalPrice]);
    const addSalesOrderItemToSalesOrder = (0, react_1.useCallback)(async (salesOrderItem) => {
        previousCart.current = salesOrderItems;
        const isExistingFixedSalesOrderItem = salesOrderItems.findIndex(i => i.type === TSalesOrderItemType_1.TSalesOrderItemType.Fixed &&
            i.productId === (salesOrderItem === null || salesOrderItem === void 0 ? void 0 : salesOrderItem.productId)) !== -1;
        // if it is an existing fixed sales order item, increment it
        if (isExistingFixedSalesOrderItem) {
            const incrementQtyIfMatches = (i) => i.productId === salesOrderItem.productId ? i.increment() : i;
            const updatedSalesOrderItems = salesOrderItems.map(incrementQtyIfMatches);
            try {
                await updateCart(updatedSalesOrderItems);
            }
            catch (e) {
                if (Object.values(constants_1.TSalesOrderUnavailableMessage).includes(e.message)) {
                    resolveError({
                        title: "Unavailable at the moment",
                        description: e.message + "\n",
                        approach: "modal",
                        hideCloseIcon: true
                    });
                }
                throw e; // rethrow to reject the promise and setError to ProductCard btn
            }
            return;
        }
        // otherwise, add it as a new sales order item
        let newCart = salesOrderItem
            ? salesOrderItems.concat(salesOrderItem)
            : salesOrderItems;
        if (orderType === TSalesOrderType_1.TSalesOrderType.Delivery) {
            // remove and place at end
            newCart = newCart.filter(i => !(0, isDeliverySalesOrderItem_1.isDeliverySalesOrderItem)(i, deliveryId) &&
                !(0, isServiceSalesOrderItem_1.isServiceSalesOrderItem)(i, serviceId));
            newCart.push(new SalesOrderItemFixed_1.SalesOrderItemFixed(deliveryProduct));
            newCart.push(new SalesOrderItemFixed_1.SalesOrderItemFixed(serviceProduct));
        }
        try {
            await updateCart(newCart);
        }
        catch (e) {
            if (Object.values(constants_1.TSalesOrderUnavailableMessage).includes(e.message)) {
                resolveError({
                    title: "Unavailable at the moment",
                    description: e.message + "\n",
                    approach: "modal",
                    hideCloseIcon: true
                });
            }
            throw e; // rethrow to reject the promise and setError to ProductCard btn
        }
    }, [salesOrderItems, deliveryProduct, orderType, updateCart]);
    // updates a particular built product in the cart
    const updateSalesOrderItemBuilt = (0, react_1.useCallback)(async (salesOrderItemBuilt, index) => {
        // set previous items just in case we have to roll back due to error
        previousCart.current = salesOrderItems;
        // update the cart at the given index with the new built product
        salesOrderItems[index] = salesOrderItemBuilt;
        // go through normal update cart process
        await updateCart(salesOrderItems);
    }, [salesOrderItems, updateCart]);
    // updates a particular product quantity in the cart
    const updateSalesOrderItemQuantity = (0, react_1.useCallback)(async (salesOrderItem, newQuantity, index) => {
        // set previous items just in case we have to roll back due to error
        previousCart.current = salesOrderItems;
        // update the cart at the given index with new quantity
        salesOrderItems[index].quantity = newQuantity;
        // go through normal update cart process
        await updateCart(salesOrderItems);
    }, [salesOrderItems, updateCart]);
    const removeSalesOrderItemFromSalesOrder = (0, react_1.useCallback)(async (salesOrderItem) => {
        previousCart.current = salesOrderItems;
        // remove specified item
        let newCart = salesOrderItems.filter(item => item !== salesOrderItem);
        // remove delivery item and see whats left
        newCart = newCart.filter(i => i && !(0, isDeliverySalesOrderItem_1.isDeliverySalesOrderItem)(i, deliveryId));
        // remove service item and see whats left
        newCart = newCart.filter(i => i && !(0, isServiceSalesOrderItem_1.isServiceSalesOrderItem)(i, serviceId));
        // if an item remains, add the delivery and service back in
        if (orderType === TSalesOrderType_1.TSalesOrderType.Delivery && newCart.length > 0) {
            newCart.push(new SalesOrderItemFixed_1.SalesOrderItemFixed(deliveryProduct));
            newCart.push(new SalesOrderItemFixed_1.SalesOrderItemFixed(serviceProduct));
        }
        await updateCart(newCart);
    }, [salesOrderItems, deliveryProduct, updateCart, orderType]);
    const updateTemporarySalesOrder = (0, react_1.useCallback)(async (temporarySalesOrder) => {
        const updatedTemporarySalesOrder = await _setTemporarySalesOrder(temporarySalesOrder);
        // use the temporary sales order to update the data
        // don't trust the client
        // this is because you could before add/remove an item to the cart, an error could occur and the cart state would not be correct
        // tho that on the server
        setSalesOrderItems(updatedTemporarySalesOrder.salesOrderItems);
        setTemporarySalesOrderId(updatedTemporarySalesOrder.salesOrderId);
        setPromotions(updatedTemporarySalesOrder.promotions);
        setTotalPrice(updatedTemporarySalesOrder.totalPrice);
    }, [
        _setTemporarySalesOrder,
        setTemporarySalesOrderError,
        isSettingTemporarySalesOrder,
        setTemporarySalesOrderId,
        setPromotions,
        setTotalPrice
    ]);
    // creates and processes the sales order
    const validateSalesOrder = (0, react_1.useCallback)(async (options, notes, deliveryAddress) => {
        const retailCustomer = client.auth.session && client.auth.session.retailCustomer;
        // sanity checks
        if (!retailLocation) {
            throw new Error(constants_1.TSalesOrderUnavailableMessage.StoreLocationUnavailableForOrder);
        }
        if (!retailCustomer) {
            throw new Error(constants_1.TSalesOrderUnavailableMessage.RetailCustomerUnavailableForOrder);
        }
        const salesOrder = new SalesOrder_1.SalesOrder();
        salesOrder.salesOrderId = temporarySalesOrderId;
        salesOrder.oneTimePayment = true;
        salesOrder.paymentToken = "NEEDS_VALIDATION";
        salesOrder.type = orderType;
        salesOrder.salesOrderItems = salesOrderItems;
        salesOrder.retailCustomer = retailCustomer;
        salesOrder.retailLocation = retailLocation;
        salesOrder.notes = notes || null;
        if (orderType === TSalesOrderType_1.TSalesOrderType.Delivery) {
            salesOrder.deliveryAddress = deliveryAddress !== null && deliveryAddress !== void 0 ? deliveryAddress : temporaryAddress;
        }
        // if the current time is before the opening then the minimum is that
        // otherwise it will be the current time up until the closing
        const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        const { openingTime, closingTime: maxTime } = retailLocation.getOperatingDetailsForTime(timestampNow);
        const orderTime = timestampNow < openingTime
            ? openingTime
            : timestampNow <= maxTime
                ? timestampNow
                : maxTime;
        if (requestedReadyTime === null) {
            // nothing was chosen so just the next available time
            salesOrder.requestedReadyTime = orderTime;
        }
        else if (requestedReadyTime < orderTime) {
            // the customer could have the UI open and let their placement time lapse of the current time so we reset it to the minimum
            // allowed order time
            salesOrder.requestedReadyTime = orderTime;
        }
        else {
            // otherwise if the placement time is set, just use that and if not just fallback to minimum allowed order time
            salesOrder.requestedReadyTime = requestedReadyTime || orderTime;
        }
        try {
            const result = await client.retailSalesOrders.validateSalesOrder(salesOrder, options);
            return result;
        }
        catch (e) {
            console.log("Failed to Validate Sales Order");
            throw e;
        }
    }, [
        retailLocation,
        salesOrderItems,
        client.auth.session,
        orderType,
        totalPrice,
        temporarySalesOrderId,
        requestedReadyTime
    ]);
    // creates and processes the sales order
    const processSalesOrder = (0, react_1.useCallback)(async (paymentToken, oneTimePayment, notes, deliveryAddress) => {
        // protect us from unexpected updates
        if (processingOrder.current) {
            console.warn("Already processing the order");
            return;
        }
        if (salesOrderItems.length === 0) {
            console.warn("Processing empty cart");
            return;
        }
        const retailCustomer = client.auth.session && client.auth.session.retailCustomer;
        // sanity checks
        if (!retailLocation) {
            throw new Error(constants_1.TSalesOrderUnavailableMessage.StoreLocationUnavailableForOrder);
        }
        if (!retailCustomer) {
            throw new Error(constants_1.TSalesOrderUnavailableMessage.RetailCustomerUnavailableForOrder);
        }
        const salesOrder = new SalesOrder_1.SalesOrder();
        salesOrder.salesOrderId = temporarySalesOrderId;
        salesOrder.oneTimePayment = oneTimePayment;
        salesOrder.paymentToken = paymentToken;
        salesOrder.type = orderType;
        salesOrder.salesOrderItems = salesOrderItems;
        salesOrder.retailCustomer = retailCustomer;
        salesOrder.retailLocation = retailLocation;
        salesOrder.notes = notes || null;
        if (orderType === TSalesOrderType_1.TSalesOrderType.Delivery) {
            salesOrder.deliveryAddress = deliveryAddress;
        }
        // if the current time is before the opening then the minimum is that
        // otherwise it will be the current time up until the closing
        const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        const { openingTime, closingTime: maxTime } = retailLocation.getOperatingDetailsForTime(timestampNow);
        const orderTime = timestampNow < openingTime
            ? openingTime
            : timestampNow <= maxTime
                ? timestampNow
                : maxTime;
        if (requestedReadyTime === null) {
            // nothing was chosen so just the next available time
            salesOrder.requestedReadyTime = orderTime;
        }
        else if (requestedReadyTime < orderTime) {
            // the customer could have the UI open and let their placement time lapse of the current time so we reset it to the minimum
            // allowed order time
            salesOrder.requestedReadyTime = orderTime;
        }
        else {
            // otherwise if the placement time is set, just use that and if not just fallback to minimum allowed order time
            salesOrder.requestedReadyTime = requestedReadyTime || orderTime;
        }
        processingOrder.current = true;
        const createdSalesOrder = await createSalesOrder(salesOrder);
        if (oneTimePayment) {
            setPaymentMethod(null);
        }
        setRequestedReadyTime(timestampNow);
        // need to return the created sales order as a promise as its used to build the summary view
        return new Promise(res => {
            res(createdSalesOrder);
        });
    }, [
        retailLocation,
        salesOrderItems,
        client.auth.session,
        orderType,
        totalPrice,
        temporarySalesOrderId,
        requestedReadyTime
    ]);
    const isUpdatingCart = (0, react_1.useMemo)(() => {
        return isSettingTemporarySalesOrder || isRetrievingTemporarySalesOrder;
    }, [isSettingTemporarySalesOrder, isRetrievingTemporarySalesOrder]);
    let previousUpdatingCartState = (0, usePreviousValue_1.usePreviousValue)(isUpdatingCart);
    let previousTotalItems = (0, usePreviousValue_1.usePreviousValue)(totalItems);
    const finishedUpdatingCart = (0, react_1.useMemo)(() => {
        if (isUpdatingCart) {
            return false;
        }
        if (previousTotalItems !== totalItems) {
            previousTotalItems = totalItems;
            return true;
        }
        if (previousUpdatingCartState !== isUpdatingCart) {
            previousUpdatingCartState = false;
            return true;
        }
        return false;
    }, [
        isUpdatingCart,
        previousUpdatingCartState,
        previousTotalItems,
        totalItems
    ]);
    // if any of the api requests have an error
    (0, react_1.useEffect)(() => {
        const hasError = setTemporarySalesOrderError ||
            retrieveTemporarySalesOrderError ||
            processSalesOrderError;
        if (hasError) {
            setHasSalesOrderError(true);
        }
    }, [
        setTemporarySalesOrderError,
        retrieveTemporarySalesOrderError,
        processSalesOrderError
    ]);
    (0, react_1.useEffect)(() => {
        setCartNoLongerExists(salesOrderItems.length === 0);
    }, [salesOrderItems]);
    (0, react_1.useEffect)(() => {
        if (!Array.isArray(salesOrderItems)) {
            return;
        }
        if (salesOrderItems.length === 0) {
            return;
        }
        if (orderType === TSalesOrderType_1.TSalesOrderType.Pickup) {
            removeSalesOrderItemFromSalesOrder(null).catch(console.error);
        }
        if (orderType === TSalesOrderType_1.TSalesOrderType.Delivery) {
            addSalesOrderItemToSalesOrder(null).catch(console.error);
        }
    }, [orderType]);
    const previousTemporaryAddress = (0, usePreviousValue_1.usePreviousValue)(temporaryAddress);
    (0, react_1.useEffect)(() => {
        if (!Array.isArray(salesOrderItems)) {
            return;
        }
        if (salesOrderItems.length === 0) {
            return;
        }
        if (previousTemporaryAddress === temporaryAddress) {
            return;
        }
        updateCart(salesOrderItems).catch(console.error);
    }, [temporaryAddress]);
    const provider = (0, react_1.useMemo)(() => {
        return {
            temporarySalesOrderId,
            salesOrderItems,
            totalPrice,
            promotions,
            processSalesOrderError,
            retrieveTemporarySalesOrderError,
            setTemporarySalesOrderError,
            isRetrievingTemporarySalesOrder,
            isSettingTemporarySalesOrder,
            isPerformingCartRecovery,
            setIsPerformingCartRecovery,
            resetCart,
            populateCart,
            getSavedTemporarySalesOrderId,
            addSalesOrderItemToSalesOrder,
            updateSalesOrderItemBuilt,
            updateSalesOrderItemQuantity,
            removeSalesOrderItemFromSalesOrder,
            processSalesOrder,
            validateSalesOrder,
            isReorder,
            setIsReorder,
            isUpdatingCart,
            hasSalesOrderError,
            setHasSalesOrderError,
            cartNoLongerExists,
            setCartNoLongerExists,
            requestedReadyTime,
            setRequestedReadyTime,
            notes,
            setNotes,
            processingOrder,
            totalItems,
            hasCart,
            finishedUpdatingCart,
            temporaryAddress,
            setTemporaryAddress
        };
    }, [
        temporarySalesOrderId,
        salesOrderItems,
        totalPrice,
        promotions,
        processSalesOrderError,
        retrieveTemporarySalesOrderError,
        setTemporarySalesOrderError,
        isRetrievingTemporarySalesOrder,
        isSettingTemporarySalesOrder,
        isPerformingCartRecovery,
        setIsPerformingCartRecovery,
        resetCart,
        populateCart,
        getSavedTemporarySalesOrderId,
        addSalesOrderItemToSalesOrder,
        updateSalesOrderItemBuilt,
        updateSalesOrderItemQuantity,
        removeSalesOrderItemFromSalesOrder,
        processSalesOrder,
        validateSalesOrder,
        isReorder,
        setIsReorder,
        isUpdatingCart,
        hasSalesOrderError,
        setHasSalesOrderError,
        cartNoLongerExists,
        setCartNoLongerExists,
        requestedReadyTime,
        setRequestedReadyTime,
        notes,
        setNotes,
        processingOrder,
        totalItems,
        hasCart,
        finishedUpdatingCart,
        temporaryAddress,
        setTemporaryAddress
    ]);
    return (React.createElement(SalesOrderContext_1.SalesOrderContext.Provider, { value: provider }, children));
};
exports.SalesOrderHandler = SalesOrderHandler;
//# sourceMappingURL=index.js.map